import rules from "./farming.json";

/**
 * The rules every crop shares (ADR-144, "Crops are data"): how long water lasts, how long a ripe crop waits for its farmer,
 * how well watering shows in quality, and the caps, rent and watch price of fields. World content in `farming.json`; every
 * number is provisional until the author sets it. Pure.
 */

/**
 * The shared farming rules.
 *
 * Times are whole numbers: `ripeHoldMs` in real milliseconds, `deadShownHours` and `waterHours` in game hours, the rest in
 * real days or coin. A watering score below `qualityPoorBelowPercent` of the growing hours moves the fine weight to poor, and a
 * score of `qualityFineAtPercent` or more moves the poor weight to fine.
 */
export interface FarmingRules {
  readonly ripeHoldMs: number;
  readonly deadShownHours: number;
  readonly waterHours: { readonly normal: number; readonly thirsty: number };
  readonly qualityFineAtPercent: number;
  readonly qualityPoorBelowPercent: number;
  readonly storeSpoilDays: number;
  readonly storeWarnDays: number;
  readonly rentDays: number;
  readonly rentCoin: number;
  readonly rentRenewDays: number;
  readonly maxRentedPatches: number;
  readonly sharedCellsPerField: number;
  readonly sharedCellsTotal: number;
  readonly farmhandsMax: number;
  readonly watchCoinPerDay: number;
  readonly watchDays: readonly number[];
  readonly canCharges: number;
}

/**
 * One thing wrong with the farming rules.
 */
export interface FarmingProblem {
  readonly field: string;
  readonly message: string;
}

/**
 * The verdict on the farming rules.
 */
export type FarmingVerdict = { readonly ok: true; readonly rules: FarmingRules } | { readonly ok: false; readonly problems: readonly FarmingProblem[] };

interface IntegerKnob {
  readonly field: string;
  readonly min: number;
  readonly max: number;
}

const DAY_MS = 24 * 60 * 60 * 1000;
const MAX_WATCH_DAYS = 90;

const INTEGER_KNOBS: readonly IntegerKnob[] = [
  { field: "ripeHoldMs", min: 0, max: 30 * DAY_MS },
  { field: "deadShownHours", min: 1, max: 2400 },
  { field: "qualityFineAtPercent", min: 1, max: 100 },
  { field: "qualityPoorBelowPercent", min: 1, max: 100 },
  { field: "storeSpoilDays", min: 1, max: 365 },
  { field: "storeWarnDays", min: 0, max: 365 },
  { field: "rentDays", min: 1, max: 365 },
  { field: "rentCoin", min: 0, max: 1_000_000 },
  { field: "rentRenewDays", min: 0, max: 365 },
  { field: "maxRentedPatches", min: 1, max: 99 },
  { field: "sharedCellsPerField", min: 1, max: 999 },
  { field: "sharedCellsTotal", min: 1, max: 999 },
  { field: "farmhandsMax", min: 0, max: 99 },
  { field: "watchCoinPerDay", min: 0, max: 1_000_000 },
  { field: "canCharges", min: 1, max: 999 }
];

const TOP_FIELDS: readonly string[] = [...INTEGER_KNOBS.map((knob) => knob.field), "waterHours", "watchDays"];
const WATER_NEEDS = ["normal", "thirsty"] as const;
const MAX_WATER_HOURS = 2400;

/**
 * The farming rules the game ships, from `farming.json`.
 */
export const DEFAULT_FARMING: FarmingRules = Object.freeze(rules as FarmingRules);

/**
 * Whether a value is a whole number from `min` to `max`.
 *
 * @param value The value.
 * @param min The least allowed.
 * @param max The most allowed.
 * @returns True for a whole number in range.
 */
function isWhole(value: unknown, min: number, max: number): value is number {
  return typeof value === "number" && Number.isInteger(value) && value >= min && value <= max;
}

/**
 * Checks the water durations, adding each problem to the list.
 *
 * @param value The `waterHours` entry.
 * @param problems The list to add to.
 */
function checkWaterHours(value: unknown, problems: FarmingProblem[]): void {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    problems.push({ field: "waterHours", message: "must be an object with normal and thirsty" });
    return;
  }
  const record = value as Record<string, unknown>;
  for (const key of Object.keys(record)) {
    if (!(WATER_NEEDS as readonly string[]).includes(key)) problems.push({ field: `waterHours.${key}`, message: "is not a water need that lasts" });
  }
  for (const need of WATER_NEEDS) {
    if (!isWhole(record[need], 1, MAX_WATER_HOURS)) problems.push({ field: `waterHours.${need}`, message: `must be a whole number of game hours from 1 to ${MAX_WATER_HOURS}` });
  }
}

/**
 * Checks the watch lengths, adding each problem to the list.
 *
 * @param value The `watchDays` entry.
 * @param problems The list to add to.
 */
function checkWatchDays(value: unknown, problems: FarmingProblem[]): void {
  const ascending = Array.isArray(value) && value.length > 0 && value.every((day, index) => isWhole(day, 1, MAX_WATCH_DAYS) && (index === 0 || day > (value[index - 1] as number)));
  if (!ascending) problems.push({ field: "watchDays", message: `must be a list of whole days from 1 to ${MAX_WATCH_DAYS}, ascending, at least one` });
}

/**
 * Checks the farming rules and names every problem.
 *
 * @param value The parsed `farming.json`.
 * @returns The rules when they are sound, else every problem with the field it is in.
 * @example
 * validateFarming(JSON.parse(text));
 */
export function validateFarming(value: unknown): FarmingVerdict {
  const problems: FarmingProblem[] = [];
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return { ok: false, problems: [{ field: "", message: "the farming rules must be an object" }] };
  }
  const record = value as Record<string, unknown>;
  for (const key of Object.keys(record)) {
    if (!TOP_FIELDS.includes(key)) problems.push({ field: key, message: "is not a farming rule" });
  }
  for (const knob of INTEGER_KNOBS) {
    if (!isWhole(record[knob.field], knob.min, knob.max)) problems.push({ field: knob.field, message: `must be a whole number from ${knob.min} to ${knob.max}` });
  }
  checkWaterHours(record.waterHours, problems);
  checkWatchDays(record.watchDays, problems);
  if (problems.length === 0) checkRelations(record as unknown as FarmingRules, problems);
  return problems.length === 0 ? { ok: true, rules: value as FarmingRules } : { ok: false, problems };
}

/**
 * Checks the rules that only make sense together, adding each problem to the list.
 *
 * @param rules Rules whose single fields have passed.
 * @param problems The list to add to.
 */
function checkRelations(rules: FarmingRules, problems: FarmingProblem[]): void {
  if (rules.qualityPoorBelowPercent > rules.qualityFineAtPercent) {
    problems.push({ field: "qualityPoorBelowPercent", message: "must not be above qualityFineAtPercent" });
  }
  if (rules.sharedCellsTotal < rules.sharedCellsPerField) problems.push({ field: "sharedCellsTotal", message: "must be at least sharedCellsPerField" });
  if (rules.storeWarnDays > rules.storeSpoilDays) problems.push({ field: "storeWarnDays", message: "must not be past storeSpoilDays" });
  if (rules.rentRenewDays > rules.rentDays) problems.push({ field: "rentRenewDays", message: "must not be past rentDays" });
}
